/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import {
    ContentContainer,
    FlexCol,
    FlexRow,
    SecurityHelper,
    toTitleCase,
} from 'sarsaparilla';
import MapCTAWrapper from 'shared-ui/maps/components/MapCTAWrapper';
import { HeroImageGalleryWithFetch } from 'shared-ui/hero-image-gallery';

import type { BreadcrumbPath, InventoryType, Rail, Tab } from './SharedVenuePage.types';

import { BreadcrumbsContainer } from './BreadcrumbsContainer';
import { ErrorNotification } from './ErrorNotification';
import { FacilityPageHeader } from './FacilityPageHeader';
import { RightRail } from './RightRail';
import { TabItems } from './TabItems';
import { FacilityAlertBannerContainer } from '../../containers/FacilityAlertBannerContainer';

export interface SharedVenuePageProps {
    map?: React.ReactNode;
    errors?: string[];
    errorRef?: React.RefObject<any>;
    title: string;
    rightRail?: Rail;
    assetId?: string;
    inventoryType?: InventoryType;
    favoriteEntityId?: string;
    favoriteEntityType?: string;
    breadcrumbPath?: BreadcrumbPath[];
    partOf?: { title: string; url: string };
    starRating?: React.ReactNode;
    cellRating?: React.ReactNode;
    tabs?: Tab[];
    activeTabIndex?: number;
    onActiveTabIndexChange?: (index: number) => void;
    isLoading?: boolean;
    hasCustomRightRail?: boolean;
    rightRailCustom?: React.ReactNode;
}

export function SharedVenuePage({
    map,
    errors,
    errorRef,
    title,
    rightRail,
    assetId,
    inventoryType,
    favoriteEntityId,
    favoriteEntityType,
    breadcrumbPath = [],
    partOf,
    starRating,
    cellRating,
    tabs,
    activeTabIndex,
    onActiveTabIndexChange,
    isLoading,
    hasCustomRightRail,
    rightRailCustom,
}: SharedVenuePageProps) {
    const isWidget = SecurityHelper.isWidgetSession();

    const renderHeader = () => {
        return (
            <>
                {/* Breadcrumbs and the main title of this page */}
                {!isWidget && (
                    <BreadcrumbsContainer path={breadcrumbPath} isLoading={isLoading} />
                )}

                <ErrorNotification
                    errors={errors}
                    errorHeader="You've encountered the following error(s):"
                    innerRef={errorRef}
                />

                {inventoryType && !isWidget && (
                    <FacilityPageHeader
                        isLoading={isLoading}
                        title={title}
                        partOf={partOf}
                        starRating={starRating}
                        cellRating={cellRating}
                        favoriteEntityId={favoriteEntityId}
                        favoriteEntityType={favoriteEntityType}
                    />
                )}
            </>
        );
    };

    // On mobile app view we should render just the RightRail as main content,
    // the permit information is already presented by the native Venue Page
    if (SecurityHelper.isMobileApp()) {
        return (
            <div className="rec-page rec-venue-page mobile-app">
                <div id="page-body">
                    <ContentContainer noTopBottomMargin hasMaxWidth>
                        {/* // On mobile app view the focus is only on registration, hide all the rest */}
                        {hasCustomRightRail ? (
                            <>{rightRailCustom}</>
                        ) : (
                            <RightRail
                                isMainRole
                                rightRail={{
                                    ...rightRail,
                                    links: [],
                                }}
                                errors={errors}
                                errorRef={errorRef}
                            />
                        )}
                    </ContentContainer>
                </div>
            </div>
        );
    }

    return (
        <div className="rec-page rec-venue-page">
            <div id="page-body">
                {assetId && !isWidget && (
                    <div role="complementary" aria-label="Hero image">
                        <HeroImageGalleryWithFetch assetId={assetId} />
                    </div>
                )}
                <FacilityAlertBannerContainer />
                <ContentContainer>
                    <FlexRow>
                        <FlexCol
                            xs={12}
                            lg={8}
                            xsOrder={1}
                            className="rec-venue-page-left-rail rec-venue-header"
                        >
                            {renderHeader()}
                        </FlexCol>
                        {/* Left Rail */}
                        <FlexCol
                            lg={8}
                            xsOrder={3}
                            lgOrder={2}
                            role="main"
                            className="rec-venue-page-left-rail"
                        >
                            <div className="rec-venue-left-rail">{renderHeader()}</div>

                            {/* Tabs */}
                            <div className="rec-venue-page-tabs-wrap">
                                <TabItems
                                    tabs={tabs}
                                    activeTabIndex={activeTabIndex}
                                    onActiveTabIndexChange={onActiveTabIndexChange}
                                />
                            </div>
                        </FlexCol>

                        {/* Right Rail */}
                        <FlexCol lg={4} xsOrder={2} lgOrder={3} id="sidebar-anchor">
                            {hasCustomRightRail ? (
                                rightRailCustom
                            ) : (
                                <RightRail rightRail={rightRail} />
                            )}
                        </FlexCol>
                    </FlexRow>
                </ContentContainer>

                {/* Bottom Map */}
                {map && (
                    <FlexRow role="complementary" aria-label="map" hasGutters={false}>
                        <FlexCol className="rec-venue-page-map-wrapper">
                            <MapCTAWrapper
                                message={`Click below to view a map of ${toTitleCase(title)}`}
                                forceDisplayMap={!isWidget}
                            >
                                <div id="map">{map}</div>
                            </MapCTAWrapper>
                        </FlexCol>
                    </FlexRow>
                )}
            </div>
        </div>
    );
}
